// ── Google Maps Platform Integration ─────────────────────────────────────────
// Provides: API key management, dynamic script loading, Places search, Geocoding

const GOOGLE_API_KEY_STORAGE = 'google_maps_api_key'

// ── API Key Management ───────────────────────────────────────────────────────
export function getGoogleApiKey(): string {
  return localStorage.getItem(GOOGLE_API_KEY_STORAGE) || ''
}

export function setGoogleApiKey(key: string): void {
  if (key.trim()) {
    localStorage.setItem(GOOGLE_API_KEY_STORAGE, key.trim())
  } else {
    localStorage.removeItem(GOOGLE_API_KEY_STORAGE)
  }
  window.dispatchEvent(new Event('google-maps-key-updated'))
}

export function hasGoogleApiKey(): boolean {
  return !!getGoogleApiKey()
}

// ── Dynamic Script Loading ───────────────────────────────────────────────────
let loadPromise: Promise<void> | null = null
let loadedKey: string | null = null

export function loadGoogleMapsScript(): Promise<void> {
  const apiKey = getGoogleApiKey()
  if (!apiKey)
    return Promise.reject(new Error('No Google Maps API key configured'))

  // If already loaded with the same key, resolve immediately
  if (loadedKey === apiKey && (window as any).google?.maps) {
    return Promise.resolve()
  }

  // If loading in progress with same key, return existing promise
  if (loadPromise && loadedKey === apiKey) return loadPromise

  loadPromise = new Promise((resolve, reject) => {
    // Remove any existing Google Maps scripts (for key changes)
    const existingScripts = document.querySelectorAll(
      'script[src*="maps.googleapis.com"]',
    )
    existingScripts.forEach((s) => s.remove())
    // Clear google maps namespace
    if ((window as any).google?.maps) {
      delete (window as any).google.maps
    }

    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geocoding&language=es&region=DO`
    script.async = true
    script.defer = true
    script.onload = () => {
      loadedKey = apiKey
      resolve()
    }
    script.onerror = () => {
      loadPromise = null
      reject(new Error('Failed to load Google Maps script'))
    }
    document.head.appendChild(script)
  })

  return loadPromise
}

// ── Google Places Search (Autocomplete) ──────────────────────────────────────
export interface PlaceResult {
  display_name: string
  lat: string
  lon: string
  place_id?: string
}

export async function searchGooglePlaces(
  query: string,
): Promise<PlaceResult[]> {
  if (!query || query.length < 2) return []

  try {
    await loadGoogleMapsScript()
  } catch {
    return []
  }

  const google = (window as any).google
  if (!google?.maps?.places) return []

  return new Promise((resolve) => {
    const service = new google.maps.places.AutocompleteService()
    service.getPlacePredictions(
      {
        input: query,
        componentRestrictions: { country: 'do' },
        types: ['establishment', 'geocode'],
        language: 'es',
      },
      (predictions: any[] | null, status: string) => {
        if (status !== 'OK' || !predictions) {
          resolve([])
          return
        }

        // Get details for each prediction to get lat/lng
        const placesService = new google.maps.places.PlacesService(
          document.createElement('div'),
        )

        const results: PlaceResult[] = []
        let pending = predictions.length

        if (pending === 0) {
          resolve([])
          return
        }

        predictions.forEach((prediction) => {
          placesService.getDetails(
            {
              placeId: prediction.place_id,
              fields: ['geometry', 'formatted_address', 'name'],
            },
            (place: any, detailStatus: string) => {
              if (detailStatus === 'OK' && place?.geometry?.location) {
                results.push({
                  display_name: place.name
                    ? `${place.name}, ${place.formatted_address}`
                    : place.formatted_address,
                  lat: String(place.geometry.location.lat()),
                  lon: String(place.geometry.location.lng()),
                  place_id: prediction.place_id,
                })
              }
              pending--
              if (pending === 0) {
                resolve(results)
              }
            },
          )
        })

        // Timeout safety
        setTimeout(() => {
          if (pending > 0) resolve(results)
        }, 5000)
      },
    )
  })
}

// ── Google Geocoding ─────────────────────────────────────────────────────────
export async function geocodeAddress(
  address: string,
): Promise<{ lat: number; lng: number } | null> {
  try {
    await loadGoogleMapsScript()
  } catch {
    return null
  }

  const google = (window as any).google
  if (!google?.maps?.Geocoder) return null

  return new Promise((resolve) => {
    const geocoder = new google.maps.Geocoder()
    geocoder.geocode(
      {
        address,
        region: 'DO',
        language: 'es',
      },
      (results: any[], status: string) => {
        if (status === 'OK' && results?.[0]?.geometry?.location) {
          resolve({
            lat: results[0].geometry.location.lat(),
            lng: results[0].geometry.location.lng(),
          })
        } else {
          resolve(null)
        }
      },
    )
  })
}

// ── Reverse Geocoding ────────────────────────────────────────────────────────
export async function reverseGeocode(
  lat: number,
  lng: number,
): Promise<string | null> {
  try {
    await loadGoogleMapsScript()
  } catch {
    return null
  }

  const google = (window as any).google
  if (!google?.maps?.Geocoder) return null

  return new Promise((resolve) => {
    const geocoder = new google.maps.Geocoder()
    geocoder.geocode(
      { location: { lat, lng } },
      (results: any[], status: string) => {
        if (status === 'OK' && results?.[0]) {
          resolve(results[0].formatted_address)
        } else {
          resolve(null)
        }
      },
    )
  })
}

// ── Validate API Key ─────────────────────────────────────────────────────────
export async function validateGoogleApiKey(key: string): Promise<boolean> {
  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?address=Santo+Domingo&key=${key}`,
    )
    const data = await response.json()
    return data.status === 'OK' || data.status === 'ZERO_RESULTS'
  } catch {
    return false
  }
}
